import {
  getProtocol as getProtocolWithDosings,
  addDosing,
  deleteDosings,
  updateBudget,
  swapListing,
  swapProduct,
  swapBundle,
  getProtocols,
  postNewProtocol,
  deleteProtocol,
  updateName,
  updateDiscount,
  share,
  removeSharedProtocol,
  updateEdit,
  updatePublic,
  postDuplicate,
  updateNumericValues,
  postProtocolsToCombine,
  updateNote,
  getDosings,
  deleteListing,
  postFees,
  postCurrency} from "../../http/protocol";
import { postDuplicate as postDuplicateMm } from "../../http/mmProtocol";
import { TGetState } from '../store';
import {
  ProtocolTable,
  addProtocol,
  addSharedWithUser,
  removeProtocol,
  removeSharedWithUser,
  updateProtocols,
  updateDiscounts,
  updateProtocolBudget,
  updateProtocolName,
  updateSharedWithUserEdit,
  updateDosingNote,
  updateDosings,
  updateProtocolPublic,
  updateFees,
  defaultProtocolSettings,
  updateCurrency} from './protocol.slice';
import { TThunkDispatch } from '../hooks';
import { IBundleSaving, ProtocolDosingRow, TListingTableRow } from '@protoplan/types';
import { validateDaysPerMonth, validateDose, validateDosesPerDay } from "../../../utils/validate";
import { getExchangeRate } from "../../http/currency";
import { calculateCostsAndRepurchases } from "@protoplan/calculate-costs";
import units from "../../units";



export const retrieveProtocolInfosAsync = () => async(dispatch: TThunkDispatch) =>
{
  const protocolsRes = await getProtocols();
  if(!protocolsRes)
    return;

  const protocols = await Promise.all(protocolsRes
    .map(async p =>
    {
      const pt = p as ProtocolTable;
      return ({ ...pt, edit: pt.edit === undefined ? undefined : Boolean(pt.edit) });
    }));

  if(!protocols)
  {
    console.log("No data returned from getProtocols");
    return;
  }

  return dispatch(updateProtocols(protocols)).payload;
};

export const retrieveDosingsAsync = (
  protocolId: number,
  savings = false) => async(dispatch: TThunkDispatch) =>
{
  const dosings = await getDosings(protocolId, savings);
  if(!dosings)
  {
    console.log("No data returned from retrieveDosingsAsync");
    return [];
  }

  return dispatch(updateDosings({ protocolId, dosings })).payload.dosings;
};

export const retrievePublicProtocolAsync = (protocolId: number) => async(dispatch: TThunkDispatch) =>
{
  const protocol = await getProtocolWithDosings(protocolId);
  if(!protocol)
  {
    console.log("No data returned from retrieveProtocol");
    return;
  }

  if(!protocol.public)
    return;

  const publicProtocol = { ...protocol, edit: false };

  dispatch(addProtocol(publicProtocol));

  return publicProtocol;
};

export const submitNewProtocolAsync = (name: string, folderId?: number, budget?: number) => async(
  dispatch: TThunkDispatch,
  getState: TGetState) =>
{
  const protocolId = await postNewProtocol(name, folderId, budget);
  const user = getState().user;
  if(!protocolId || !user)
    return;

  dispatch(addProtocol({
    ...defaultProtocolSettings,
    protocolId,
    folderId,
    userId: Number(user.userId),
    username: String(user.username),
    name,
    budget,
    currencyId: user.currencyId,
    currencyCode: 'GBP',
    currencySymbol: user.currencySymbol })).payload;

  return protocolId;
}

export const addToProtocolAsync = (
  protocolId: number,
  listingId?: number,
  productId?: number,
  appliedDiscountIds?: number[]) => async(dispatch: TThunkDispatch) =>
{
  return addDosing(protocolId, listingId, productId, appliedDiscountIds)
    .catch(error => console.log("Error adding to protocol:", error))
    .then(() => dispatch(retrieveDosingsAsync(protocolId)));
};

export const swapIndividualListingIntoProtocolAsync = (
  listing: TListingTableRow,
  dosingId: number,
  protocolId: number) => async(dispatch: TThunkDispatch) =>
{
  return swapListing(listing.listingId, dosingId)
    .catch(error => console.log("Error swapping into protocol:", error))
    .then(async dosings =>
    {
      if(!dosings)
        return;

      dispatch(updateDosings({ protocolId, dosings }));
    });
};

export const swapBundleSavingIntoProtocolAsync = (
  bundleSaving: IBundleSaving,
  protocolId: number) => async(dispatch: TThunkDispatch) =>
{
  const dosings = await swapBundle(bundleSaving, protocolId);
  if(!dosings)
    return;

  dispatch(updateDosings({ protocolId, dosings }));
};

export const swapFromSameVendorAsync = (
  dosingId: number,
  protocolId: number,
  productId: number) => async(dispatch: TThunkDispatch) =>
{
  const listing = await swapProduct(dosingId, protocolId, productId);

  if(listing)
    dispatch(swapIndividualListingIntoProtocolAsync(listing, dosingId, protocolId));

  return listing?.listingId;
};

function reducePriorities<T>(
  dosingIdsToRemoveFromProtocol: number[],
  dosings: ({ dosingId: number, priority: number } & T)[])
{
  const prioritiesToRemove = dosings
    .filter(row => dosingIdsToRemoveFromProtocol.includes(row.dosingId))
    .map(row => row.priority);
  const sortedPrioritiesToRemove = prioritiesToRemove.sort((a, b) => a - b);

  const idsToReducePriority: { [dosingId: number]: number; } = {};

  sortedPrioritiesToRemove.forEach(priority =>
  {
    dosings
      .filter(row => row.priority > priority)
      .forEach(row => idsToReducePriority[row.dosingId] =
        (idsToReducePriority[row.dosingId] || row.priority) - 1);
  });

  if(!Object.keys(idsToReducePriority).length)
    return dosings;

  const newProtocol = dosings.map(row => ({ ...row }));
  Object.keys(idsToReducePriority).forEach(key =>
  {
    const dosingId = Number(key);
    const rowToReducePriority = newProtocol.find(row => row.dosingId === dosingId);
    if(!rowToReducePriority)
      return;
    rowToReducePriority.priority = idsToReducePriority[dosingId];
    newProtocol[newProtocol.indexOf(rowToReducePriority)] = rowToReducePriority;
  });

  return newProtocol;
}

export const deleteDosingsFromProtocolAsync = (
  protocolId: number,
  dosingIds?: number[]) => async(dispatch: TThunkDispatch, getState: TGetState) =>
{
  const protocol = getState().protocol.protocols.find(p => p.protocolId === protocolId)?.dosings;
  if(!protocol)
    return;

  const dosingIdsToRemove =
    dosingIds ||
    protocol.filter(row => row.checked).map(row => row.dosingId);
  if(!dosingIdsToRemove.length)
    return;

  const newDosings = reducePriorities(dosingIdsToRemove, protocol)
    .filter(row => !dosingIdsToRemove.includes(row.dosingId));
  dispatch(updateDosings({ protocolId, dosings: newDosings }));

  return deleteDosings(dosingIdsToRemove)
    .catch(error => console.log("Error deleting supplement from protocol:", error));
};

export const updateBudgetAsync = (
  budget: number,
  protocolId: number) => async(dispatch: TThunkDispatch) =>
{
  dispatch(updateProtocolBudget({ budget, protocolId }));

  return updateBudget(budget, protocolId);
};

export const deleteProtocolAsync = (protocolId: number) => async(dispatch: TThunkDispatch) =>
{
  dispatch(removeProtocol(protocolId));

  return deleteProtocol(protocolId);
};

export const removeSharedProtocolAsync = (protocolId: number, userId?: number) => async(dispatch: TThunkDispatch) =>
{
  if(userId)
    dispatch(removeSharedWithUser({ protocolId, userId }));
  else
    dispatch(removeProtocol(protocolId));

  return removeSharedProtocol(protocolId, userId);
};

export const renameProtocolAsync = (name: string, protocolId: number) => async(dispatch: TThunkDispatch) =>
{
  dispatch(updateProtocolName({ protocolId, name }));

  return updateName(name, protocolId);
};

export const toggleDiscountsAsync = (protocolId: number, discounts: {
  dosingId: number,
  discountId: number,
  bundleId?: number}[]) => async(
  dispatch: TThunkDispatch, getState: TGetState) =>
{
  let protocol = getState().protocol.protocols.find(p => p.protocolId === protocolId) as ProtocolTable;
  if(!protocol)
    return;

  discounts.forEach(discount =>
  {
    const dosingIds = discount.bundleId ?
      protocol.dosings?.filter(d => d.bundleId === discount.bundleId).map(d => d.dosingId) :
      [discount.dosingId];

    protocol.dosings?.forEach(dosing =>
    {
      if(!dosingIds?.includes(dosing.dosingId))
        return;

      const discounts = getState().protocol.protocols
        .find(p => p.protocolId === protocolId)?.dosings
        ?.find(d => d.dosingId === dosing.dosingId)?.discounts || [];

      const newDiscounts = discounts.map(d =>
      {
        if(d.discountId !== discount.discountId)
          return d;

        // Toggle on server
        updateDiscount(dosing.dosingId, d.discountId);

        return ({ ...d, applied: !d.applied });
      });

      dispatch(updateDiscounts({ protocolId, dosingId: dosing.dosingId, discounts: newDiscounts }));
    });
  });

  // Recalculate costs
  protocol = getState().protocol.protocols.find(p => p.protocolId === protocolId) as ProtocolTable;
  if(!protocol.dosings)
    return;
  const dosings = await calculateCostsAndRepurchases(protocol.dosings, getExchangeRate);

  dispatch(updateDosings({ protocolId: protocol.protocolId, dosings }));
};

export const shareProtocolAsync = (protocolId: number, userId?: number, email?: string) => async(
  dispatch: TThunkDispatch) =>
{
  const result = await share(protocolId, userId, email);
  if(!result?.success)
    console.log("Failed to share protocol");

  if(result?.username && userId)
    dispatch(addSharedWithUser({ protocolId, userId, username: result.username }));

  return result;
};

export const updateEditAsync = (protocolId: number, userId: number, edit: boolean) => async(
  dispatch: TThunkDispatch) =>
{
  dispatch(updateSharedWithUserEdit({ protocolId, userId, edit }));

  await updateEdit(protocolId, userId, edit);
};

export const updatePublicAsync = (protocolId: number, public_: boolean) => async(dispatch: TThunkDispatch) =>
{
  dispatch(updateProtocolPublic({ protocolId, public: public_ }));

  await updatePublic(protocolId, public_);
};

export const duplicateAsync = (protocolId: number, folderId?: number, name?: string) => async(
  dispatch: TThunkDispatch) =>
{
  const protocol = await postDuplicate(protocolId, folderId, name);
  if(!protocol)
    return;

  dispatch(addProtocol({ ...protocol, edit: true }));

  return protocol.protocolId;
};

export const duplicateMmProtocolAsync = (mmProtocolId: number) => async(
  dispatch: TThunkDispatch) =>
{
  const result = await postDuplicateMm(mmProtocolId);
  if(!result)
    return;

  return dispatch(addProtocol(result)).payload;
};

export const updateNumericValueAsync = (
  protocolId: number,
  dosingId: number,
  label: keyof ProtocolDosingRow,
  value: number) => async(dispatch: TThunkDispatch, getState: TGetState) =>
{
  const protocol = getState().protocol.protocols.find(p => p.protocolId === protocolId);
  const dosings = protocol?.dosings;
  if(!dosings)
    return;
  const dosing = dosings.find(d => d.dosingId === dosingId);

  if(
    !dosingId || !protocol || !dosing ||
    (label === 'dose' && !validateDose(value)) ||
    (label === 'dosesPerDay' && !validateDosesPerDay(value)) ||
    (label === 'daysPerMonth' && !validateDaysPerMonth(value)))
  {
    return;
  }

  let newDosings = dosings.map(r => ({ ...r, [label]: ((r.dosingId === dosingId) ? value : r[label]) }));

  // Update dose and dose unit -> amount unit factor
  if(label === 'doseUnitId')
  {
    const newDosing = newDosings.find(d =>d.dosingId === dosingId);
    if(!newDosing)
      return;

    const amountConversionFactor = dosing.amountUnitId ? await units.getFactor(
      value,
      dosing.amountUnitId,
      dosing.productId ? [dosing.productId]: undefined) : undefined;

    newDosing.factor = amountConversionFactor;

    const doseConversionFactor = await units.getFactor(
      dosing.doseUnitId,
      value,
      dosing.productId ? [dosing.productId]: undefined);
    const newDose = (doseConversionFactor && doseConversionFactor !== 1) ?
      dosing.dose * doseConversionFactor :
      undefined;
    const doseValid = newDose !== undefined && validateDose(newDose);

    if(doseValid)
    {
      newDosing.dose = newDose;
      await updateNumericValues([{ dosingId, value: newDose }], 'dose');
    }
  }

  newDosings = await calculateCostsAndRepurchases(newDosings, getExchangeRate);

  dispatch(updateDosings({ protocolId, dosings: newDosings }));

  return await updateNumericValues([{ dosingId, value }], label);
}

export const updateDosingNoteAsync = (
  protocolId: number,
  rowIndex: number,
  note?: string) => async(dispatch: TThunkDispatch, getState: TGetState) =>
{
  const dosings = getState().protocol.protocols.find(p => p.protocolId === protocolId)?.dosings;
  if(!dosings)
    return;
  const dosingId = dosings[rowIndex].dosingId;

  dispatch(updateDosingNote({ protocolId, dosingId, note }));

  return await updateNote(dosingId, note);
}

export const combineProtocolsAsync = (protocols: {
  protocolId?: number,
  mmProtocolId?: number,
  weighting: number }[]) => async(dispatch: TThunkDispatch, getState: TGetState) =>
{
  const user = getState().user;
  if(!user.userId)
    return;

  const protocolId = await postProtocolsToCombine(protocols, user.userId);
  if(!protocolId)
    return;

  const protocol = await getProtocolWithDosings(protocolId);
  if(!protocol)
    return;

  if(!user.premium)
    dispatch(updateProtocols([]));

  return dispatch(addProtocol(protocol)).payload;
}

export const updateListingInProtocolsAsync = (listingId: number) => async(
  dispatch: TThunkDispatch, getState: TGetState) =>
{
  const protocolIds = getState().protocol.protocols
    .reduce((ids, p) => p.dosings?.map(r => r.listingId).flat().includes(listingId) ?
      ids.concat(p.protocolId) :
      ids, <number[]>[]);

  if(protocolIds.length)
    await Promise.all(protocolIds.map(id => dispatch(retrieveDosingsAsync(id))));
}

export const removeListingAsync = (protocolId: number, dosingId: number) => async(dispatch: TThunkDispatch) =>
{
  return deleteListing(dosingId)
    .then(() => dispatch(retrieveDosingsAsync(protocolId)));
};

export const updateFeesAsync = (protocolId: number, fees: boolean) => async(
  dispatch: TThunkDispatch) =>
{
  dispatch(updateFees({ protocolId, fees }));

  await postFees(protocolId, fees);
};

export const updateCurrencyAsync = (
  protocolId: number,
  currencyId: number,
  currencyCode: string,
  currencySymbol: string) => async(
  dispatch: TThunkDispatch) =>
{
  dispatch(updateCurrency({ protocolId, currencyId, currencyCode, currencySymbol }));

  await postCurrency(protocolId, currencyId)
    .then(() => dispatch(retrieveDosingsAsync(protocolId)));
}
